import type { DraggableAttributes } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { useFocusWithin, useHover } from '@react-aria/interactions';
import { DotsHandleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import React from 'react';
import { Asset as _Asset } from './asset';
import { AssetActions } from './asset.actions';
import { AssetContextMenu } from './asset.context-menu';
import { AssetImage } from './asset.image';
import { AssetLabel } from './asset.label';
import { BUTTON_TOP_INSET } from './constants';
import { draggableStyles } from './styles';
import type { AssetDraggableProps } from './types';

/**
 * A draggable version of the Asset component with a visible drag handle
 * Supports both draggable and sortable modes with dnd-kit
 * The drag handle appears on hover and follows dnd-kit best practices for scrollable lists
 */
export const AssetDraggableWithHandle = ({
  id,
  children,
  className,
  onDragStart,
  onDragEnd,
  disabled = false,
  mode = 'draggable',
  data,
  forceDragHandle = false,
  isViewOnly = false,
  buttonTopOffset,
  ...assetProps
}: AssetDraggableProps) => {
  const [isFocusWithin, setFocusWithin] = React.useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  });

  const { hoverProps, isHovered } = useHover({});

  const isDisabled = disabled || isViewOnly;

  const draggableResult = useDraggable({
    id,
    disabled: isDisabled || mode !== 'draggable',
    data,
  });

  const sortableResult = useSortable({
    id,
    disabled: isDisabled || mode !== 'sortable',
    data,
  });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    mode === 'sortable'
      ? sortableResult
      : { ...draggableResult, transition: undefined, isDragging: draggableResult.isDragging };

  // Reset focus after drag ends so the handle hides properly
  const wasDragging = React.useRef(false);
  React.useEffect(() => {
    if (wasDragging.current && !isDragging) {
      setFocusWithin(false);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
    wasDragging.current = isDragging;
  }, [isDragging]);

  const renderDragHandle = React.useCallback(
    ({ attributes, listeners }: { attributes?: DraggableAttributes; listeners?: SyntheticListenerMap }) => {
      if (isViewOnly) return null;

      const topOffset = buttonTopOffset ?? BUTTON_TOP_INSET;

      return (
        <button
          type="button"
          className={cx(
            'absolute left-1',
            'flex items-center flex-shrink-0 justify-center w-9 h-9 sm:w-18 sm:h-18',
            'group',
            'bg-overlay-action hover:bg-overlay-action-hover border-none',
            isFocusWithin && 'outline-none ring-2 ring-gray-modern-600',
            // Handle cursor changes
            'cursor-grab',
            isDragging && 'cursor-grabbing',
            // Don't show drag handle when disabled
            isDisabled && !forceDragHandle && 'hidden',
            // Show handle when hovering OR when dragging OR when forced (drag overlay)
            isHovered || isDragging || forceDragHandle || isFocusWithin
              ? 'opacity-100'
              : 'opacity-0 pointer-events-none',
          )}
          style={{
            // Critical for preventing scroll on touch devices per dnd-kit recommendations
            touchAction: 'none',
            top: topOffset,
            ...dragHandleStyle,
          }}
          {...(listeners ?? {})}
          {...(attributes ?? {})}
          aria-label="Drag handle"
        >
          <DotsHandleIcon className="text-quinary group-hover:text-quinary-hover" />
        </button>
      );
    },
    [isHovered, isDragging, isDisabled, isViewOnly, forceDragHandle, isFocusWithin, buttonTopOffset],
  );

  const transformStyle = transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined;

  const containerStyle: React.CSSProperties = {
    ...draggableStyles,
    transform: transformStyle,
    transition,
    opacity: isDragging ? 0 : 1,
    zIndex: isDragging ? 10 : 0,
    touchAction: 'auto',
    position: 'relative',
    ...(buttonTopOffset != null && ({ '--asset-button-top': `${buttonTopOffset}px` } as React.CSSProperties)),
  };

  const dragHandleStyle: React.CSSProperties = {};

  return (
    <div {...hoverProps} {...focusWithinProps} data-happo-hover>
      <div
        ref={setNodeRef}
        className={className}
        style={containerStyle}
        data-asset-id={id}
        aria-roledescription={isViewOnly ? undefined : mode === 'sortable' ? 'sortable' : 'draggable'}
      >
        <_Asset {...assetProps} buttonTopOffset={buttonTopOffset} isViewOnly={isViewOnly} data-asset-id={id}>
          {children}
        </_Asset>
        {renderDragHandle({ attributes, listeners })}
      </div>
    </div>
  );
};

AssetDraggableWithHandle.Image = AssetImage;
AssetDraggableWithHandle.Actions = AssetActions;
AssetDraggableWithHandle.ContextMenu = AssetContextMenu;
AssetDraggableWithHandle.Label = AssetLabel;
